<template>
  <section v-if="shadow && connected" class="rounded-lg border p-4 space-y-2" aria-label="Square booking payment permissions">
    <h3 class="font-semibold">Booking payment access</h3>
    <p v-if="busy" role="status" class="text-sm">Checking your Square connection…</p>
    <template v-else-if="status === 'ready'">
      <p role="status" class="text-sm text-green-700 dark:text-green-300">Square has the permissions needed for booking payments.</p>
    </template>
    <template v-else-if="status === 'reconnect_required'">
      <p role="status" class="text-sm">Reconnect Square to enable booking payments. Your current connection is missing required access or needs authorization again.</p>
      <button v-if="owner" type="button" class="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white" @click="$emit('reconnect')">Reconnect Square</button>
      <p v-else class="text-sm">Ask your shop owner to reconnect Square.</p>
    </template>
    <p v-else-if="status === 'not_connected'" role="status" class="text-sm">Connect Square to check booking payment access.</p>
    <p v-else-if="status === 'restricted'" role="status" class="text-sm">Your shop owner or administrator can check Square access.</p>
    <template v-else>
      <p role="status" class="text-sm">{{ status === 'signed_out' ? 'Please sign in again to check your Square connection.' : 'Square access could not be verified right now. Please try again.' }}</p>
    </template>
    <button v-if="!busy && canCheck && status !== 'signed_out'" type="button" class="text-sm underline" @click="check">{{ status === 'ready' ? 'Check again' : 'Retry permission check' }}</button>
    <p v-if="!pilot" class="text-sm text-gray-500">Booking checkout is currently being piloted with S3 Tint. This check does not activate checkout for your shop.</p>
  </section>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase/config';
import store from '../../../store';
const props = defineProps({ connected: { type: Boolean, default: false } });
defineEmits(['reconnect']);
const shadow = computed(() => store.state.userConfig?.project === 'shadow');
const pilot = computed(() => store.state.userConfig?.profile === 's3Tint-invXT2bn');
const owner = computed(() => store.state.accountContext?.role === 'owner');
const canCheck = computed(() => ['owner', 'admin'].includes(store.state.accountContext?.role));
const status = ref('unavailable');
const busy = ref(false);
let requestId = 0;
async function check() {
  const id = ++requestId;
  if (!shadow.value || !props.connected) { busy.value = false; return; }
  if (!canCheck.value) { status.value = 'restricted'; busy.value = false; return; }
  busy.value = true;
  try {
    const result = await httpsCallable(functions, 'shadowSquarePermissions')({});
    if (id !== requestId) return;
    status.value = result.data.status;
  } catch (error) {
    if (id !== requestId) return;
    status.value = error?.code === 'functions/unauthenticated' ? 'signed_out'
      : error?.code === 'functions/permission-denied' ? 'restricted' : 'unavailable';
  } finally { if (id === requestId) busy.value = false; }
}
watch(() => [shadow.value, props.connected, store.state.accountOwnerId, canCheck.value], check, { immediate: true });
onBeforeUnmount(() => { requestId++; });
</script>
